import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Search, Filter, Check, X, Leaf, AlertTriangle,
  Thermometer, ArrowRight, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Kit() {
  const navigate = useNavigate();
  const [herbs, setHerbs] = useState([]);
  const [userKit, setUserKit] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTemp, setFilterTemp] = useState('all'); // all, cald, rece, neutru
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHerbs();
    // Load user kit from localStorage
    const savedKit = localStorage.getItem('acupedia_kit');
    if (savedKit) {
      setUserKit(JSON.parse(savedKit));
    }
  }, []);

  const fetchHerbs = async () => {
    try {
      const response = await axios.get(`${API}/herbs`);
      setHerbs(response.data);
    } catch (error) {
      console.error('Error fetching herbs:', error);
      toast.error('Eroare la încărcarea plantelor');
    } finally {
      setLoading(false);
    }
  };

  const toggleHerbInKit = (herbId) => {
    let newKit;
    if (userKit.includes(herbId)) {
      newKit = userKit.filter(id => id !== herbId);
      toast.success('Plantă eliminată din trusă');
    } else {
      newKit = [...userKit, herbId];
      toast.success('Plantă adăugată în trusă');
    }
    setUserKit(newKit);
    localStorage.setItem('acupedia_kit', JSON.stringify(newKit));
  };

  const selectAll = () => {
    const allHerbIds = herbs.map(h => h.id);
    setUserKit(allHerbIds);
    localStorage.setItem('acupedia_kit', JSON.stringify(allHerbIds));
    toast.success('Toate plantele au fost adăugate');
  };

  const clearAll = () => {
    setUserKit([]);
    localStorage.setItem('acupedia_kit', JSON.stringify([]));
    toast.success('Trusa a fost golită');
  };

  // Normalize text for search (remove diacritics)
  const normalizeText = (text) => {
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ș/g, 's').replace(/ț/g, 't')
      .replace(/ă/g, 'a').replace(/â/g, 'a').replace(/î/g, 'i');
  };

  const filteredHerbs = herbs.filter(herb => {
    const normalizedSearch = normalizeText(searchQuery);
    const matchesSearch = normalizeText(herb.name_ro).includes(normalizedSearch) ||
                         normalizeText(herb.name_latin).includes(normalizedSearch);
    const matchesTemp = filterTemp === 'all' || herb.energetic_temp === filterTemp;
    return matchesSearch && matchesTemp;
  });

  const getTempColor = (temp) => {
    switch(temp) {
      case 'cald': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'rece': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      default: return 'bg-stone-100 text-stone-700 border-stone-200';
    }
  };

  const getTempLabel = (temp) => {
    switch(temp) {
      case 'cald': return 'Încălzitor';
      case 'rece': return 'Răcoritor';
      default: return 'Neutru';
    }
  };

  const hasSafetyFlags = (herb) => {
    const flags = herb.safety_flags || {};
    return Object.values(flags).some(v => v === true);
  };

  return (
    <div className="py-8 md:py-12 animate-fade-in">
      <div className="container-acupedia">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-acupedia-teal flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="section-header mb-0">Trusa Mea de Plante</h1>
            </div>
            <p className="section-subheader">
              Selectează plantele pe care le ai disponibile în kitul tău
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-4 py-2 text-sm">
              {userKit.length} / {herbs.length} selectate
            </Badge>
            <Button
              variant="outline"
              onClick={() => navigate('/intake')}
              disabled={userKit.length === 0}
              className="btn-secondary"
              data-testid="continue-to-intake-btn"
            >
              Continuă
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              placeholder="Caută plantă după nume..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-acupedia"
              data-testid="search-herbs-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-stone-400" />
            <select
              value={filterTemp}
              onChange={(e) => setFilterTemp(e.target.value)}
              className="input-acupedia px-4 pr-8 cursor-pointer"
              data-testid="filter-temp-select"
            >
              <option value="all">Toate temperaturile</option>
              <option value="cald">Încălzitoare</option>
              <option value="rece">Răcoritoare</option>
              <option value="neutru">Neutre</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
              Selectează toate
            </Button>
            <Button variant="outline" size="sm" onClick={clearAll} data-testid="clear-all-btn">
              Golește
            </Button>
          </div>
        </div>

        {/* Herbs Grid */}
        {loading ? (
          <div className="herbs-grid">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="skeleton h-48 rounded-xl"></div>
            ))}
          </div>
        ) : (
          <div className="herbs-grid animate-stagger">
            {filteredHerbs.map((herb) => {
              const isInKit = userKit.includes(herb.id);
              
              return (
                <div
                  key={herb.id}
                  className={`herb-card card-acupedia p-5 cursor-pointer ${
                    isInKit ? 'ring-2 ring-acupedia-teal bg-acupedia-teal/5' : ''
                  }`}
                  onClick={() => toggleHerbInKit(herb.id)}
                  data-testid={`herb-card-${herb.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-heading text-lg text-stone-800 mb-0.5">
                        {herb.name_ro}
                      </h3>
                      <p className="text-xs text-stone-500 italic">
                        {herb.name_latin}
                      </p>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                      isInKit ? 'bg-acupedia-teal text-white' : 'bg-stone-100 text-stone-400'
                    }`}>
                      {isInKit ? <Check className="w-5 h-5" /> : <Leaf className="w-4 h-4" />}
                    </div>
                  </div>

                  {/* Temperature & Meridians */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`text-xs px-2 py-1 rounded-full border ${getTempColor(herb.energetic_temp)}`}>
                      {getTempLabel(herb.energetic_temp)}
                    </span>
                    {herb.meridians?.slice(0, 2).map((meridian, i) => (
                      <span key={i} className="meridian-badge text-xs">
                        {meridian}
                      </span>
                    ))}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-stone-600 line-clamp-2 mb-3">
                    {herb.description}
                  </p>

                  {/* Dosage & Safety */}
                  <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                    <span className="font-mono text-xs text-stone-500">
                      {herb.dosage_min_g}-{herb.dosage_max_g}g
                    </span>
                    {hasSafetyFlags(herb) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 text-amber-600">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="text-xs">Atenție</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Există contraindicații - verifică profilul</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>

                  {/* Forms */}
                  <div className="flex gap-1 mt-2">
                    {herb.forms?.map((form, i) => (
                      <span key={i} className="text-xs text-stone-400 bg-stone-50 px-2 py-0.5 rounded">
                        {form}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filteredHerbs.length === 0 && !loading && (
          <div className="text-center py-16">
            <Leaf className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-stone-600 mb-2">
              Nu am găsit plante
            </h3>
            <p className="text-stone-500">
              Încearcă să modifici criteriile de căutare
            </p>
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-12 p-6 bg-acupedia-teal/5 rounded-xl border border-acupedia-teal/20">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-acupedia-teal flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-heading text-lg text-stone-800 mb-2">
                Cum funcționează kitul?
              </h4>
              <p className="text-sm text-stone-600 mb-3">
                Acupedia generează rețete doar din plantele pe care le ai disponibile în kit. 
                Cu cât ai mai multe plante, cu atât mai multe opțiuni de rețete vei primi.
              </p>
              <ul className="text-sm text-stone-600 space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-acupedia-gold" />
                  Plantele încălzitoare (portocaliu) tonifică și energizează
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-acupedia-gold" />
                  Plantele răcoritoare (albastru) calmează și detoxifică
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-acupedia-gold" />
                  Plantele neutre sunt versatile și sigure
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
